import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  fetchMetadataKeys,
  fetchMetadataValues,
  fetchCriteriaValues,
} from "../api";
import type {
  MetadataKey,
  MetadataValue,
  TagFilter,
  CriteriaFilter,
  DateRangeFilter,
  ScoreFilter,
} from "../types";
import { METADATA_VALUE_LIMIT } from "../constants";
import {
  buildMetadataFilterContext,
  buildCriteriaFilterContext,
} from "../utils/metadataFilters";
import type { ScoreMetric } from "../utils/chartData";
import {
  INSIGHTS_NAMESPACE,
  readPersistedFilters,
  writePersistedFilters,
} from "../utils/filterPersistence";
import { useSearchParams } from "react-router-dom";
import type { PersistedFilterState } from "../utils/filterPersistence";
import type { DateWindow } from "../utils/dateWindow";
import { resolveWindow, windowFromDateRange } from "../utils/dateWindow";
import {
  fromSearchParams,
  hasViewParams,
  toSearchParams,
  viewParamsKey,
} from "../utils/viewUrl";
import { useTodayKey } from "./useTodayKey";
import { getImpersonationDomain } from "../../impersonation/storage";
import { readRequestCache, writeRequestCache } from "../utils/requestCache";

const LAST_30_DAYS: DateWindow = { kind: "rolling", amount: 30, unit: "day" };

/** Options for the shared page-filter hook. */
export interface PageFiltersOptions {
  /** sessionStorage namespace so each page keeps an independent slice. */
  namespace?: string;
  /** Window used only when nothing is persisted yet for this namespace. */
  defaultWindow?: DateWindow;
}

/**
 * Filter / metadata / criteria state for the insights + traces views.
 * Owns no row data — charts pull aggregates directly via useChartAggregate;
 * metadataError surfaces failures from metadata/criteria loads only.
 *
 * The filter + view slice is persisted to sessionStorage under `namespace` (see
 * utils/filterPersistence) so navigating away and back keeps the view (#1402).
 * A chart drill-down seeds the Traces namespace before navigating there.
 */
export function useInsightsFilters(options?: PageFiltersOptions) {
  const namespace = options?.namespace ?? INSIGHTS_NAMESPACE;
  const defaultWindow =
    options && "defaultWindow" in options
      ? options.defaultWindow
      : LAST_30_DAYS;

  const [searchParams, setSearchParams] = useSearchParams();

  // Precedence on mount: a slice in the URL wins, then the persisted slice,
  // then the defaults. A link someone was sent describes exactly what they
  // should see, so it must not be overridden by whatever this tab was last
  // looking at; with no slice in the URL the persisted one is still "where I
  // was last" and keeps navigating away and back intact (#1402).
  const restoredRef = useRef<ReturnType<typeof readPersistedFilters>>();
  if (restoredRef.current === undefined) {
    restoredRef.current = hasViewParams(searchParams)
      ? fromSearchParams(searchParams)
      : readPersistedFilters(namespace);
  }
  const restored = restoredRef.current;

  const [filters, setFilters] = useState<TagFilter[]>(
    () => restored?.filters ?? [],
  );
  const [criteriaFilter, setCriteriaFilter] = useState<CriteriaFilter | undefined>(
    () => restored?.criteriaFilter,
  );
  // The window is held as intent; the concrete range is derived below. A slice
  // persisted before `dateWindow` existed carries resolved dates, which can only
  // be read as absolute — it stops drifting from here on.
  const [dateWindow, setDateWindow] = useState<DateWindow | undefined>(() => {
    if (!restored) return defaultWindow;
    return restored.dateWindow ?? windowFromDateRange(restored.dateRangeFilter);
  });
  // `todayKey` is pinned until the user accepts a rollover, so this memo is
  // stable between renders. Resolving against `new Date()` inline would mint a
  // new range object every render, changing the query params and refetching in
  // a loop.
  const { todayKey, isStale: dayHasRolled, refresh: refreshWindow } =
    useTodayKey();
  const dateRangeFilter = useMemo(
    () => resolveWindow(dateWindow, todayKey),
    [dateWindow, todayKey],
  );
  // Only a rolling window can go stale — an absolute one resolves to the same
  // dates whatever day it is read on, so the rollover is not news.
  const isWindowStale = dayHasRolled && dateWindow?.kind === "rolling";
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter | undefined>(
    () => restored?.scoreFilter,
  );
  // View dimension, not a filter: drives the grouped section only. Deliberately
  // kept out of the ungrouped charts' params and the metadata-values context.
  const [groupByKey, setGroupByKey] = useState<string | undefined>(
    () => restored?.groupByKey,
  );
  // Which metric the Score Trends lines plot (avg | p25 | p10). Persisted view
  // state; Insights-only (ignored by the Traces page).
  const [scoreMetric, setScoreMetric] = useState<ScoreMetric>(
    () => restored?.scoreMetric ?? "avg",
  );
  // Metadata keys are reference data shared by both pages. Cache them in the
  // module-level store (stale-while-revalidate), scoped by impersonation domain,
  // so navigating away and back doesn't re-block the whole Insights view on a
  // reload. The per-mount ref used before was destroyed on unmount, so every
  // return to the page flashed the loading skeleton even though the charts were
  // cached (#1402 follow-up).
  const metaKeysCacheKey = `metadata-keys:${getImpersonationDomain() || "self"}`;
  const [metadataKeys, setMetadataKeys] = useState<MetadataKey[]>(
    () => readRequestCache<MetadataKey[]>(metaKeysCacheKey) ?? [],
  );
  const [loadingMetadataKeys, setLoadingMetadataKeys] = useState(
    () => readRequestCache<MetadataKey[]>(metaKeysCacheKey) === undefined,
  );
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [metadataValues, setMetadataValues] = useState<
    Record<string, MetadataValue[]>
  >({});
  const [criteria, setCriteria] = useState<string[]>([]);
  const [loadingCriteria, setLoadingCriteria] = useState(false);

  // Per-dropdown "still valid?" markers: the serialised filter context the
  // currently-held values were fetched under. A re-open whose context matches
  // skips the re-fetch (instant, no stale-then-update flicker); a mismatch
  // re-fetches. Not a results cache — just a validity check.
  const valuesFetchedForRef = useRef<Record<string, string>>({});
  const criteriaFetchedForRef = useRef<string | null>(null);

  // Load metadata keys, seeding instantly from the persistent cache when present
  // and revalidating in the background so a return visit paints immediately
  // rather than re-blocking on a fetch.
  useEffect(() => {
    let cancelled = false;
    const cached = readRequestCache<MetadataKey[]>(metaKeysCacheKey);
    if (cached === undefined) setLoadingMetadataKeys(true);
    const loadMetadataKeys = async () => {
      try {
        const keys = await fetchMetadataKeys();
        if (cancelled) return;
        setMetadataKeys(keys);
        writeRequestCache(metaKeysCacheKey, keys);
      } catch (err) {
        if (cancelled) return;
        // Keep showing cached keys on a background failure; only surface the
        // error when there was nothing cached to show.
        if (cached === undefined) {
          const message =
            err instanceof Error ? err.message : "Failed to load metadata keys";
          setMetadataError(message);
        }
        console.error("Failed to load metadata keys:", err);
      } finally {
        if (!cancelled) setLoadingMetadataKeys(false);
      }
    };
    loadMetadataKeys();
    return () => {
      cancelled = true;
    };
  }, [metaKeysCacheKey]);

  // Fetches a key's values against the current filter context, stripping the
  // dropdown's own key so a tag never self-filters its own options (design doc
  // 002 ticket 2c). Skips the fetch when the held values already reflect the
  // current filters, so re-opening an unchanged dropdown is instant; otherwise
  // clears the key first so the dropdown shows its spinner rather than stale
  // values while the re-fetch is in flight.
  const loadMetadataValuesForKey = useCallback(
    async (key: string) => {
      const context = buildMetadataFilterContext(key, {
        filters,
        criteriaFilter,
        dateRangeFilter,
        scoreFilter,
      });
      const contextHash = JSON.stringify(context ?? null);
      if (valuesFetchedForRef.current[key] === contextHash) {
        return;
      }
      setMetadataValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      try {
        const valuesResponse = await fetchMetadataValues(
          [key],
          context,
          METADATA_VALUE_LIMIT,
        );
        const values = valuesResponse[key] || [];
        setMetadataValues((prev) => ({ ...prev, [key]: values }));
        valuesFetchedForRef.current[key] = contextHash;
      } catch (err) {
        // Leave the key absent (no recorded hash) so a re-open retries.
        setMetadataValues((prev) => ({ ...prev, [key]: [] }));
        console.error(`Failed to load metadata values for ${key}:`, err);
      }
    },
    [filters, criteriaFilter, dateRangeFilter, scoreFilter],
  );

  // Loads the criteria list narrowed by the active tag/date/score filters,
  // stripping the criteria filter itself so the list never self-filters. Same
  // validity guard as the tag dropdowns: skips the fetch when the held criteria
  // already reflect the current filters. Loaded lazily on dropdown open.
  const loadCriteria = useCallback(async () => {
    const context = buildCriteriaFilterContext({
      filters,
      criteriaFilter,
      dateRangeFilter,
      scoreFilter,
    });
    const contextHash = JSON.stringify(context ?? null);
    if (criteriaFetchedForRef.current === contextHash) {
      return;
    }
    setLoadingCriteria(true);
    try {
      const values = await fetchCriteriaValues(context);
      setCriteria(values);
      criteriaFetchedForRef.current = contextHash;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load criteria";
      setMetadataError(message);
      console.error("Failed to load criteria:", err);
    } finally {
      setLoadingCriteria(false);
    }
  }, [filters, criteriaFilter, dateRangeFilter, scoreFilter]);

  const addFilter = useCallback((filter: TagFilter) => {
    setFilters((prev) => {
      const filtered = prev.filter((f) => f.key !== filter.key);
      return [...filtered, filter];
    });
  }, []);

  const removeFilter = useCallback((key: string) => {
    setFilters((prev) => prev.filter((f) => f.key !== key));
  }, []);

  const updateFilter = useCallback((key: string, filter: Partial<TagFilter>) => {
    setFilters((prev) =>
      prev.map((f) => (f.key === key ? { ...f, ...filter } : f)),
    );
  }, []);

  // Bulk-apply a whole persisted slice (a saved view). Sets every field at once;
  // the persist effect below then writes it to the current slice as usual.
  const applySlice = useCallback((state: PersistedFilterState) => {
    setFilters(state.filters ?? []);
    setCriteriaFilter(state.criteriaFilter);
    setDateWindow(state.dateWindow);
    setScoreFilter(state.scoreFilter);
    setGroupByKey(state.groupByKey);
    setScoreMetric(state.scoreMetric ?? "avg");
  }, []);

  // Jumping to a saved view is a destination, not an edit, so it earns a
  // history entry; incremental filter tweaks replace instead, or Back becomes
  // useless (design doc 033, U2).
  //
  // Armed only when the jump actually lands somewhere else. Clicking the tab
  // that is already active re-applies the slice you are on, which writes no
  // URL — and a flag left armed would be spent by the *next* ordinary filter
  // edit, pushing an entry and turning Back into "undo one filter".
  const pushNextRef = useRef(false);
  const applyView = useCallback(
    (state: PersistedFilterState) => {
      // Compared on the owned params so a pure reordering of the query string
      // doesn't read as a destination, and with the same `scoreMetric` default
      // `applySlice` is about to apply, or the prediction misses.
      const next = toSearchParams(
        { ...state, scoreMetric: state.scoreMetric ?? "avg" },
        searchParams,
      );
      pushNextRef.current =
        viewParamsKey(next) !== viewParamsKey(searchParams);
      applySlice(state);
    },
    [applySlice, searchParams],
  );

  // Persist the filter + view slice so the page keeps its state across
  // navigation (#1402). Metadata/criteria caches are intentionally not
  // persisted — they are reference data re-fetched lazily on demand.
  // The slice this hook last wrote to the URL. Anything else arriving in
  // `searchParams` came from outside — Back/Forward, or a pasted link — and is
  // adopted rather than overwritten. Compared on the owned params only, so
  // another feature's write (`?trace=` on the Traces page) isn't mistaken for a
  // slice change. Seeded at mount so the initial URL isn't re-adopted.
  const lastWrittenRef = useRef<string>(viewParamsKey(searchParams));

  // Write the live slice over `params`, leaving the params this hook doesn't
  // own alone. Called from both directions below, so the URL is only ever
  // written in one place.
  const mirrorToUrl = useCallback(
    (params: URLSearchParams) => {
      const next = toSearchParams(
        { filters, criteriaFilter, dateWindow, scoreFilter, groupByKey, scoreMetric },
        params,
      );
      if (next.toString() === params.toString()) return;
      const push = pushNextRef.current;
      pushNextRef.current = false;
      lastWrittenRef.current = viewParamsKey(next);
      setSearchParams(next, { replace: !push });
    },
    [
      filters,
      criteriaFilter,
      dateWindow,
      scoreFilter,
      groupByKey,
      scoreMetric,
      setSearchParams,
    ],
  );

  useEffect(() => {
    const current = viewParamsKey(searchParams);
    if (current === lastWrittenRef.current) return;
    lastWrittenRef.current = current;
    if (hasViewParams(searchParams)) {
      applySlice(fromSearchParams(searchParams));
      return;
    }
    // The slice left the URL with nothing replacing it — navigating to the bare
    // path, which is what the sidebar's own "Insights" link does while you are
    // already on the page. The filters stay on screen (#1402), so put them back
    // in the URL; otherwise the address bar describes a view nobody is looking
    // at and Copy link hands out a link to the unfiltered page.
    mirrorToUrl(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Mirror the slice into the URL so it can be bookmarked and shared (#1416).
  useEffect(() => {
    mirrorToUrl(searchParams);
    // `searchParams` is deliberately not a dep: this effect reacts to state
    // changes, and the sync effect above owns the other direction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, criteriaFilter, dateWindow, scoreFilter, groupByKey, scoreMetric]);

  useEffect(() => {
    writePersistedFilters(namespace, {
      filters,
      criteriaFilter,
      dateWindow,
      scoreFilter,
      groupByKey,
      scoreMetric,
    });
  }, [
    namespace,
    filters,
    criteriaFilter,
    dateWindow,
    scoreFilter,
    groupByKey,
    scoreMetric,
  ]);

  return {
    metadataKeys,
    metadataValues,
    loadingMetadataKeys,
    loadMetadataValuesForKey,
    filters,
    addFilter,
    removeFilter,
    updateFilter,
    applyView,
    criteriaFilter,
    setCriteriaFilter,
    dateWindow,
    setDateWindow,
    /** Derived from `dateWindow` — what the query layer consumes. */
    dateRangeFilter,
    /** True once the local date has moved past the resolved day. */
    isWindowStale,
    refreshWindow,
    scoreFilter,
    setScoreFilter,
    groupByKey,
    setGroupByKey,
    scoreMetric,
    setScoreMetric,
    criteria,
    loadingCriteria,
    loadCriteria,
    metadataError,
  };
}
